import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  IconButton, 
  Tooltip, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField, 
  CircularProgress, 
  Divider, 
  Alert, 
  Snackbar,
  Grid,
  Tabs,
  Tab,
  Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { format, Locale } from 'date-fns';
import { tr, enUS } from 'date-fns/locale';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { getReservations, updateReservationStatus } from '../../../services/reservationsApi';
import { mapReservationStatusToCalendar } from '../utils/reservationMapper';
import { useTranslation } from 'react-i18next';
import GuideButton from '../../common/guide/GuideButton';
import { TFunction } from 'i18next';

// FullCalendar components
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';

// Icons
import { 
  Today as TodayIcon, 
  ChevronLeft as ChevronLeftIcon, 
  ChevronRight as ChevronRightIcon,
  ViewDay as ViewDayIcon,
  ViewWeek as ViewWeekIcon,
  CalendarMonth as MonthIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
  AccessTime as ClockIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Comment as CommentIcon
} from '@mui/icons-material';

// Reservation interface
interface Reservation {
  id: string;
  title: string;
  start: string;
  end: string;
  status: 'pending' | 'approved' | 'rejected' | 'consumed';
  customerName: string;
  customerPhone: string;
  customerEmail?: string;
  notes?: string;
  createdAt: string;
  availabilitySlotId?: string; // Reference to the availability slot
  color?: string;
  borderColor?: string;
  textColor?: string;
  source?: 'ticket';
  orderNumericId?: number;
}

interface BranchInfo {
  name: string;
  address: string;
  phone: string;
  email: string;
}

// Calendar container style component
const CalendarContainer = styled(Box)(({ theme }) => ({
  height: 'calc(100vh - 230px)',
  maxHeight: '800px',
  overflow: 'auto',
  width: '100%',
  '& .fc': {
    '--fc-border-color': theme.palette.divider,
    '--fc-button-text-color': theme.palette.primary.main,
    '--fc-button-bg-color': 'transparent',
    '--fc-button-border-color': theme.palette.primary.main,
    '--fc-button-hover-bg-color': theme.palette.primary.main,
    '--fc-button-hover-text-color': theme.palette.common.white,
    '--fc-button-active-bg-color': theme.palette.primary.dark,
    '--fc-today-bg-color': `${theme.palette.primary.main}10`,
    '--fc-page-bg-color': theme.palette.background.paper,
    '--fc-neutral-bg-color': theme.palette.background.default,
    fontFamily: theme.typography.fontFamily,
  },
  '& .fc .fc-toolbar': {
    flexWrap: 'wrap',
    gap: '10px',
    marginBottom: '1rem'
  },
  '& .fc .fc-toolbar-title': {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: theme.palette.text.primary
  },
  '& .fc-theme-standard td, .fc-theme-standard th': {
    borderColor: theme.palette.divider
  },
  '& .fc .fc-day-today': {
    backgroundColor: `${theme.palette.primary.main}10`,
  },
  '& .fc-day-header': {
    fontWeight: 600,
    padding: '10px 0'
  },
  '& .fc-event': {
    borderRadius: '4px',
    padding: '2px 4px',
    fontSize: '0.85rem',
    cursor: 'pointer',
    borderWidth: '0',
    boxShadow: '0 2px 5px rgba(0,0,0,0.08)',
    transition: 'transform 0.1s ease-in-out, box-shadow 0.1s ease-in-out',
    '&:hover': {
      transform: 'translateY(-1px) scale(1.01)',
      boxShadow: '0 4px 8px rgba(0,0,0,0.12)'
    }
  },
  // Status-based colors
  '& .fc-event.status-approved': {
    backgroundColor: '#4caf50', // Green for approved
    borderColor: '#2e7d32'
  },
  '& .fc-event.status-pending': {
    backgroundColor: '#2196f3', // Blue for pending
    borderColor: '#1565c0'
  },
  '& .fc-event.status-rejected': {
    backgroundColor: '#f44336', // Red for rejected
    borderColor: '#c62828'
  },
  '& .fc-event.status-consumed': {
    backgroundColor: '#7e57c2', // Purple for consumed
    borderColor: '#5e35b1'
  },
  '& .fc-event.status-conflict': {
    backgroundColor: '#ff9800', // Orange for conflicts
    borderColor: '#e65100'
  },
  '& .fc-col-header-cell': {
    padding: '8px 0'
  },
  // Mobile responsiveness
  [theme.breakpoints.down('sm')]: {
    '& .fc-toolbar': {
      flexDirection: 'column',
      alignItems: 'center'
    },
    '& .fc-dayGridMonth-view .fc-daygrid-day-events': {
      margin: '0 2px'
    },
    '& .fc-header-toolbar .fc-toolbar-chunk': {
      marginBottom: '8px'
    }
  }
}));

// Custom toolbar component
const CalendarToolbar = ({ 
  calendarRef, 
  view, 
  setView,
  t,
  locale
} : { 
  calendarRef: React.RefObject<FullCalendar | null>, 
  view: string, 
  setView: (view: string) => void,
  t: TFunction<'other'>,
  locale: Locale
}) => {
  
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  
  useEffect(() => {
    const calendar = calendarRef.current;
    if (calendar) {
      const api = calendar.getApi();
      setCurrentDate(api.getDate());
    }
  }, [calendarRef, view]);
  
  const handlePrev = () => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.prev();
      setCurrentDate(calendarApi.getDate());
    }
  };

  const handleNext = () => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.next();
      setCurrentDate(calendarApi.getDate());
    }
  };

  const handleToday = () => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.today();
      setCurrentDate(calendarApi.getDate());
    }
  };

  const changeView = (newView: string) => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.changeView(newView);
      setView(newView);
    }
  };
  
  const formatCurrentDate = () => {
    if (view === 'timeGridDay') {
      return format(currentDate, 'd MMMM yyyy, EEEE', { locale });
    }
    return format(currentDate, 'MMMM yyyy', { locale });
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={t('reservations.requests.toolbar.previous')}>
            <IconButton onClick={handlePrev} color="primary" size="small">
              <ChevronLeftIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('reservations.requests.toolbar.today')}>
            <IconButton onClick={handleToday} color="primary" size="small">
              <TodayIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('reservations.requests.toolbar.next')}>
            <IconButton onClick={handleNext} color="primary" size="small">
              <ChevronRightIcon />
            </IconButton>
          </Tooltip>
          
          <Typography variant="h5" sx={{ fontWeight: 600, ml: 2 }}>
            {formatCurrentDate()}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={t('reservations.requests.toolbar.dayView')}>
            <IconButton 
              onClick={() => changeView('timeGridDay')} 
              color={view === 'timeGridDay' ? 'secondary' : 'default'}
              size="small"
            >
              <ViewDayIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('reservations.requests.toolbar.weekView')}>
            <IconButton 
              onClick={() => changeView('timeGridWeek')} 
              color={view === 'timeGridWeek' ? 'secondary' : 'default'}
              size="small"
            >
              <ViewWeekIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('reservations.requests.toolbar.monthView')}>
            <IconButton 
              onClick={() => changeView('dayGridMonth')} 
              color={view === 'dayGridMonth' ? 'secondary' : 'default'}
              size="small"
            >
              <MonthIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

// Status chip component
const StatusChip = ({ status, t }: { status: string; t: TFunction<'other'> }) => {
  if (status === 'approved') {
    return <Chip 
      label={t('reservations.requests.status.approved')} 
      color="success" 
      size="small" 
      icon={<CheckCircleIcon />} 
    />;
  } else if (status === 'rejected') {
    return <Chip 
      label={t('reservations.requests.status.rejected')} 
      color="error" 
      size="small" 
      icon={<CancelIcon />} 
    />;
  } else if (status === 'pending') {
    return <Chip 
      label={t('reservations.requests.status.pending')} 
      color="primary" 
      size="small" 
      icon={<ClockIcon />} 
    />;
  } else if (status === 'consumed') {
    return <Chip
      label={t('reservations.requests.status.consumed')}
      color="secondary"
      size="small"
      icon={<CheckCircleIcon />}
    />;
  } else if (status === 'conflict') {
    return <Chip 
      label={t('reservations.requests.status.conflict')} 
      color="warning" 
      size="small" 
      icon={<InfoIcon />} 
    />;
  }
  return null;
};

// Tab panel component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`reservation-tabpanel-${index}`}
      aria-labelledby={`reservation-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const BranchReservationRequests: React.FC = () => {
  const { t, i18n } = useTranslation('other');
  const locale = i18n.language?.startsWith('tr') ? tr : enUS;
  const { branchId } = useParams<{ branchId: string }>();
  const { currentUser, branches } = useAuth();
  const navigate = useNavigate();
  
  // Refs
  const calendarRef = useRef<FullCalendar | null>(null);
  
  // States
  const [view, setView] = useState<string>('dayGridMonth');
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);
  const [branchInfo, setBranchInfo] = useState<BranchInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState<number>(0);
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
  const [confirmAction, setConfirmAction] = useState<'approve' | 'reject' | null>(null);
  
  // Tab change handler
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Check for conflicts between reservations
  const checkForConflicts = (reservations: Reservation[]): Reservation[] => {
    // Approved/consumed reservations
    const approvedReservations = reservations.filter(
      (res) => res.status === 'approved' || res.status === 'consumed'
    );
    
    // Check each pending reservation for conflicts with approved ones
    return reservations.map(reservation => {
      if (reservation.status !== 'pending') return reservation;
      
      const start = new Date(reservation.start).getTime();
      const end = new Date(reservation.end).getTime();
      
      // Check if this pending reservation conflicts with any approved reservation
      const hasConflict = approvedReservations.some(approved => {
        const approvedStart = new Date(approved.start).getTime();
        const approvedEnd = new Date(approved.end).getTime();
        
        // Check for overlap
        return (
          (start >= approvedStart && start < approvedEnd) || // Start time is within approved slot
          (end > approvedStart && end <= approvedEnd) || // End time is within approved slot
          (start <= approvedStart && end >= approvedEnd) // Completely contains the approved slot
        );
      });
      
      // If there's a conflict, flag it in the UI
      if (hasConflict) {
        return {
          ...reservation,
          color: '#ff9800', // Orange for conflicts
          borderColor: '#e65100',
          className: 'status-conflict'
        };
      }
      
      return reservation;
    });
  };

  // Load branch data and reservations
  useEffect(() => {
    const loadBranchData = async () => {
      if (!currentUser?.uid || !branchId) {
        setError(t('reservations.requests.errors.requiredInfoMissing'));
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      const selectedBranch = branches.find((item) => item.id === branchId);
      if (selectedBranch) {
        setBranchInfo({
          name: selectedBranch.name,
          address: selectedBranch.address || '',
          phone: selectedBranch.phone || '',
          email: '',
        });
      } else {
        setBranchInfo(null);
      }

      try {
        const ticketRows = await getReservations({
          type: 'ticket',
          branchId,
          page: 1,
          pageSize: 500,
        });

        const ticketReservations: Reservation[] = ticketRows.rows.map((ticket) => {
          const dateSource = ticket.reservationDate || ticket.createdAt;
          const startDate = dateSource && dateSource.includes('T')
            ? new Date(dateSource)
            : new Date(`${dateSource}T10:00:00`);
          const safeStart = Number.isNaN(startDate.getTime())
            ? new Date(ticket.createdAt)
            : startDate;
          const safeEnd = new Date(safeStart.getTime() + 60 * 60 * 1000);
          const normalizedStatus: Reservation['status'] = mapReservationStatusToCalendar(ticket.status);

          return {
            id: `ticket-${ticket.id}`,
            title: t('reservations.requests.onlineTicketTitle', { customer: ticket.customerName || t('reservations.requests.customerFallback') }),
            start: safeStart.toISOString(),
            end: safeEnd.toISOString(),
            status: normalizedStatus,
            customerName: ticket.customerName || '-',
            customerPhone: ticket.phone || '-',
            customerEmail: ticket.email || '',
            notes: '',
            createdAt: ticket.createdAt,
            color:
              normalizedStatus === 'approved'
                ? '#4caf50'
                : normalizedStatus === 'rejected'
                  ? '#f44336'
                  : normalizedStatus === 'consumed'
                    ? '#7e57c2'
                    : '#2196f3',
            className: `status-${normalizedStatus}`,
            source: 'ticket',
            orderNumericId: ticket.id
          };
        });

        const reservationArray = checkForConflicts(ticketReservations);
        setReservations(reservationArray);
      } catch (err) {
        console.error("Error loading reservation data:", err);
        setError(t('reservations.requests.errors.loadFailed'));
      } finally {
        setIsLoading(false);
      }
    };
    
    loadBranchData();
  }, [branchId, branches, currentUser, t]);
  
  // Handle event click (reservation selection)
  const handleEventClick = (info: any) => {
    const reservation = reservations.find(r => r.id === info.event.id);
    if (reservation) {
      setSelectedReservation(reservation);
      setOpenModal(true);
    }
  };
  
  // Close modal handler
  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedReservation(null);
  };
  
  // Close error notification
  const handleCloseError = () => {
    setError(null);
  };
  
  // Go back to branches page
  const handleBack = () => {
    navigate(`/reservations/branch/${branchId}`);
  };
  
  // Approve reservation
  const handleApproveReservation = () => {
    setConfirmAction('approve');
    setShowConfirmDialog(true);
  };
  
  // Reject reservation
  const handleRejectReservation = () => {
    setConfirmAction('reject');
    setShowConfirmDialog(true);
  };
  
  // Confirm approve/reject action
  const handleConfirmAction = async () => {
    if (!selectedReservation || !confirmAction || !branchId) return;
    
    try {
      const nextStatus = confirmAction === 'approve' ? 'approved' : 'rejected';

      if (selectedReservation.orderNumericId) {
        await updateReservationStatus(
          selectedReservation.orderNumericId,
          confirmAction === 'approve' ? t('reservations.requests.status.approved') : t('reservations.requests.status.rejected')
        );
      }

      setReservations(prev =>
        prev.map(item =>
          item.id === selectedReservation.id
            ? {
                ...item,
                status: nextStatus,
                color: nextStatus === 'approved' ? '#4caf50' : '#f44336',
                className: `status-${nextStatus}`,
              }
            : item
        )
      );
      
      console.log(`Reservation ${confirmAction}d:`, selectedReservation.id);
      
      // Close dialogs
      setShowConfirmDialog(false);
      handleCloseModal();
      
    } catch (error) {
      console.error(`Error ${confirmAction}ing reservation:`, error);
      setError(confirmAction === 'approve' ? t('reservations.requests.errors.approveFailed') : t('reservations.requests.errors.rejectFailed'));
    }
  };

  return (
    <Paper sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title={t('reservations.requests.actions.back')}>
            <IconButton onClick={handleBack}>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Typography variant="h5" component="h1" sx={{ ml: 1 }}>
            {t('reservations.requests.headerTitle', { branch: branchInfo?.name || t('reservations.requests.branchFallback') })}
          </Typography>
          <GuideButton guideKey="reservations.requests" size="small" sx={{ ml: 0.5 }} />
        </Box>
      </Box>
      
      {/* Branch info */}
      {branchInfo && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2">
            {t('reservations.requests.branchAddress', { address: branchInfo.address })}
          </Typography>
          {branchInfo.phone && (
            <Typography variant="body2">
              {t('reservations.requests.branchPhone', { phone: branchInfo.phone })}
            </Typography>
          )}
        </Box>
      )}
      
      {/* Legend */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: '#4caf50', mr: 1 }} />
          <Typography variant="body2">{t('reservations.requests.status.approved')}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: '#2196f3', mr: 1 }} />
          <Typography variant="body2">{t('reservations.requests.status.pending')}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: '#ff9800', mr: 1 }} />
          <Typography variant="body2">{t('reservations.requests.status.conflict')}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: '#f44336', mr: 1 }} />
          <Typography variant="body2">{t('reservations.requests.status.rejected')}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: '#7e57c2', mr: 1 }} />
          <Typography variant="body2">{t('reservations.requests.status.consumed')}</Typography>
        </Box>
      </Box>
      
      <Divider sx={{ my: 2 }} />
      
      {/* Error notification */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={handleCloseError}>
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
      
      {/* Calendar area */}
      <CalendarContainer>
        <CalendarToolbar calendarRef={calendarRef} view={view} setView={setView} t={t} locale={locale} />
        
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '500px' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView={view}
              headerToolbar={false}
              events={reservations}
              eventTimeFormat={{
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
              }}
              eventClick={handleEventClick}
              dayMaxEvents={true}
              weekends={true}
              allDaySlot={false}
              slotMinTime="06:00:00"
              slotMaxTime="23:00:00"
              slotDuration="00:30:00"
              businessHours={{
                daysOfWeek: [1, 2, 3, 4, 5, 6, 0],
                startTime: '09:00',
                endTime: '18:00',
              }}
              locale={i18n.language?.startsWith('tr') ? 'tr' : 'en'}
              buttonText={{
                today: t('reservations.requests.calendar.today'),
                month: t('reservations.requests.calendar.month'),
                week: t('reservations.requests.calendar.week'),
                day: t('reservations.requests.calendar.day'),
                list: t('reservations.requests.calendar.list')
              }}
            />
          </>
        )}
      </CalendarContainer>
      
      {/* Reservation detail modal */}
      <Dialog 
        open={openModal} 
        onClose={handleCloseModal} 
        maxWidth="md" 
        fullWidth
      >
        {selectedReservation && (
          <>
            <DialogTitle>
              {t('reservations.requests.modal.title')}
              <Box sx={{ mt: 1 }}>
                <StatusChip status={selectedReservation.status} t={t} />
              </Box>
            </DialogTitle>
            
            <DialogContent dividers>
              <Box sx={{ mt: 1 }}>
                <Tabs 
                  value={tabValue}
                  onChange={handleTabChange}
                  aria-label={t('reservations.requests.modal.tabsAriaLabel')}
                >
                  <Tab 
                    icon={<InfoIcon fontSize="small" />} 
                    iconPosition="start" 
                    label={t('reservations.requests.modal.tabs.reservationInfo')} 
                  />
                  <Tab 
                    icon={<PersonIcon fontSize="small" />} 
                    iconPosition="start" 
                    label={t('reservations.requests.modal.tabs.customerInfo')} 
                  />
                </Tabs>
                
                <TabPanel value={tabValue} index={0}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="h6">{selectedReservation.title}</Typography>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                        <ClockIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary">{t('reservations.requests.modal.fields.startTime')}</Typography>
                          <Typography variant="body1">
                            {format(new Date(selectedReservation.start), 'dd MMMM yyyy HH:mm', { locale })}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                        <ClockIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary">{t('reservations.requests.modal.fields.endTime')}</Typography>
                          <Typography variant="body1">
                            {format(new Date(selectedReservation.end), 'dd MMMM yyyy HH:mm', { locale })}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    
                    {selectedReservation.notes && (
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                          <CommentIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          <Box>
                            <Typography variant="body2" color="text.secondary">{t('reservations.requests.modal.fields.notes')}</Typography>
                            <Typography variant="body1">{selectedReservation.notes}</Typography>
                          </Box>
                        </Box>
                      </Grid>
                    )}
                    
                    <Grid item xs={12}>
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          {t('reservations.requests.modal.fields.createdAt', { date: format(new Date(selectedReservation.createdAt), 'dd MMMM yyyy HH:mm', { locale }) })}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </TabPanel>
                
                <TabPanel value={tabValue} index={1}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                        <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary">{t('reservations.requests.modal.fields.customerName')}</Typography>
                          <Typography variant="body1">{selectedReservation.customerName}</Typography>
                        </Box>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                        <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary">{t('reservations.requests.modal.fields.phone')}</Typography>
                          <Typography variant="body1">{selectedReservation.customerPhone}</Typography>
                        </Box>
                      </Box>
                    </Grid>
                    
                    {selectedReservation.customerEmail && (
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                          <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          <Box>
                            <Typography variant="body2" color="text.secondary">{t('reservations.requests.modal.fields.email')}</Typography>
                            <Typography variant="body1">{selectedReservation.customerEmail}</Typography>
                          </Box>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </TabPanel>
              </Box>
            </DialogContent>
            
            <DialogActions sx={{ justifyContent: selectedReservation.status === 'pending' ? 'space-between' : 'flex-end' }}>
              {selectedReservation.status === 'pending' && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button 
                    onClick={handleRejectReservation} 
                    color="error" 
                    variant="outlined"
                    startIcon={<CancelIcon />}
                  >
                    {t('reservations.requests.actions.reject')}
                  </Button>
                  <Button 
                    onClick={handleApproveReservation} 
                    color="success" 
                    variant="contained"
                    startIcon={<CheckCircleIcon />}
                  >
                    {t('reservations.requests.actions.approve')}
                  </Button>
                </Box>
              )}
              <Button onClick={handleCloseModal}>{t('reservations.requests.actions.close')}</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      
      {/* Confirmation dialog for approve/reject */}
      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
      >
        <DialogTitle>
          {confirmAction === 'approve' ? t('reservations.requests.confirmDialog.titleApprove') : t('reservations.requests.confirmDialog.titleReject')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {confirmAction === 'approve' 
              ? t('reservations.requests.confirmDialog.messageApprove')
              : t('reservations.requests.confirmDialog.messageReject')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)}>{t('reservations.requests.actions.cancel')}</Button>
          <Button 
            onClick={handleConfirmAction} 
            color={confirmAction === 'approve' ? 'success' : 'error'} 
            variant="contained"
          >
            {confirmAction === 'approve' ? t('reservations.requests.actions.approve') : t('reservations.requests.actions.reject')}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default BranchReservationRequests; 